/**
 * Página `/calendario-aseador` — vista mensual de calendario solo con
 * los aseos del aseador logueado (los que le asignaron + los que tomó).
 *
 * - Reusa el endpoint `GET /api/aseos/calendario` (el backend filtra por
 *   `id_usuario === req.user.id` cuando el rol es aseador). Los aseos
 *   ABIERTOS (sin tomar) NO aparecen acá: el aseador aún no los tiene que
 *   hacer hasta tomarlos — los descubre y los toma en la pestaña "Aseos
 *   abiertos" de /aseos, no en su calendario personal.
 * - Render: Ant Design `<Calendar>` mensual con badges por día.
 * - Click en un día → modal con la lista de aseos de ese día (propiedad,
 *   horario, estado, urgencia, observaciones).
 *
 * Pensada para que el aseador planifique su semana/mes a un golpe de
 * vista — la tabla de `/aseos` es lineal y no muestra distribución
 * temporal. Esta vista es complementaria, no la reemplaza.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Calendar,
  Modal,
  Spin,
  Tag,
  Typography,
} from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useSidebar } from "../../hooks/useSidebar";
import { apiFetch } from "../../services/apiClient";
import { formatPropertyName } from "../../utils/propertyDisplay";
import AseoCodeTag from "../../Components/AseoCodeTag";
import EstadiaCodeTag from "../../Components/EstadiaCodeTag";
import AseoNotesSection from "../aseos/components/AseoNotesSection";
import NotasContenedor from "../aseos/components/NotasContenedor";
import { useEsTactil } from "../aseos/components/useMediaQuery";
import "../../Css/notas-movil.css";
import "./calendarioAseador.css";
import { ahoraChile } from "../../utils/fechaChile";

dayjs.locale("es");

const { Text } = Typography;

type AseoCalendar = {
  id: number;
  // Código de estadía AF del arriendo vinculado (reserva_id → booking.id).
  // NULL = aseo manual/aledaño. El backend (getCleaningsCalendar) ya lo devuelve.
  reservaId?: number | null;
  idPropiedad: number;
  idAseador: number | null;
  estado: "pendiente" | "en_proceso" | "completado" | "pagado" | string;
  fecha: string;
  horaInicio: string | null;
  urgencia: "am" | "pm" | "critico 11:00" | "11:00" | string | null;
  observaciones: string | null;
  sueldoAseador: number | null;
  // Round 2026-05-11: bono/descuento per-aseo eliminados.
  // OJO: el backend (getCleaningsCalendar) devuelve la propiedad con la llave
  // `nombre`, NO `nombrepropiedad`. Leer la llave equivocada dejaba el modal
  // mostrando "Aseo #878" en vez del nombre de la propiedad (fix 2026-07-05).
  propiedad?: {
    id: number;
    nombre: string;
  } | null;
};

type CalendarResp = {
  success?: boolean;
  data?: AseoCalendar[];
};

const ESTADO_COLOR: Record<string, "default" | "processing" | "success" | "warning" | "error"> = {
  pendiente: "warning",
  en_proceso: "processing",
  completado: "success",
  pagado: "success",
};

const ESTADO_LABEL: Record<string, string> = {
  pendiente: "Pendiente",
  en_proceso: "En proceso",
  completado: "Completado",
  pagado: "Pagado",
};

const URGENCIA_COLOR: Record<string, string> = {
  am: "orange",
  pm: "blue",
  "critico 11:00": "red",
  "11:00": "red",
};

const fmtClp = (n: number | null | undefined): string => {
  if (n == null) return "—";
  return `$${Math.round(n).toLocaleString("es-CL")}`;
};

export default function CalendarioAseador() {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  // Con el dedo cada chip mide >= 44 px (ver .nm-chip), así que en la celda
  // cabe uno: el resto va tras «+N más», que abre el listado del día.
  const maxChips = useEsTactil() ? 1 : 3;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aseos, setAseos] = useState<AseoCalendar[]>([]);
  const [refMonth, setRefMonth] = useState<Dayjs>(ahoraChile());
  // Aseo cuyo modal de NOTAS está abierto (mismo modal/esquema que la tabla de
  // /aseos: AseoNotesSection). Día cuyo listado completo está abierto (al tocar
  // el día o "+N más"). Round 2026-07-06.
  const [notesAseo, setNotesAseo] = useState<AseoCalendar | null>(null);
  const [dayModal, setDayModal] = useState<Dayjs | null>(null);

  const load = useCallback(async (month: Dayjs) => {
    setLoading(true);
    setError(null);
    // Cargamos un rango amplio (mes ± 1) para cubrir las celdas vecinas que
    // muestra el calendario al inicio/final del mes.
    const from = month.startOf("month").subtract(7, "day").format("YYYY-MM-DD");
    const to = month.endOf("month").add(7, "day").format("YYYY-MM-DD");
    const resp = await apiFetch<CalendarResp>(
      `/aseos/calendario?from=${from}&to=${to}`,
      { errorMsg: "Error al cargar el calendario" },
    );
    if (!resp) {
      setError("No pude cargar tu calendario. Intenta de nuevo.");
      setAseos([]);
    } else {
      setAseos(Array.isArray(resp.data) ? resp.data : []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load(refMonth);
  }, [load, refMonth]);

  // Indexar aseos por fecha (YYYY-MM-DD) para lookups O(1) por celda.
  const aseosPorFecha = useMemo(() => {
    const map = new Map<string, AseoCalendar[]>();
    for (const a of aseos) {
      const key = String(a.fecha).slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    }
    // Ordenar por hora dentro del día.
    map.forEach((arr) =>
      arr.sort((x, y) =>
        String(x.horaInicio ?? "").localeCompare(String(y.horaInicio ?? "")),
      ),
    );
    return map;
  }, [aseos]);

  const renderCellContent = (date: Dayjs) => {
    const key = date.format("YYYY-MM-DD");
    const items = aseosPorFecha.get(key) ?? [];
    if (items.length === 0) return null;
    return (
      <ul className="ca-cell-list">
        {items.slice(0, maxChips).map((a) => {
          const tieneNota = !!(a.observaciones && a.observaciones.trim());
          return (
            <li key={a.id}>
              {/* Chip informativo y clickeable: hora · propiedad · monto (+ 📄 si
                  hay instrucciones). Tocarlo abre las notas del aseo. */}
              <div
                role="button"
                className="nm-chip"
                data-testid="nm-chip-aseo"
                title="Ver notas del aseo"
                onClick={(e) => {
                  e.stopPropagation();
                  setNotesAseo(a);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  cursor: "pointer",
                  borderRadius: 6,
                  padding: "1px 5px",
                  background: "#f0f9ff",
                  border: "1px solid #bae6fd",
                  overflow: "hidden",
                }}
              >
                <Badge status={ESTADO_COLOR[a.estado] ?? "default"} />
                {/* Código de aseo compacto (aseos.id): identidad propia del aseo,
                    visible de un vistazo. El «Estadía #N» del arriendo vinculado
                    va en el detalle (modal del día y modal de notas). */}
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#7c3aed",
                    whiteSpace: "nowrap",
                  }}
                >
                  #{a.id}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {a.horaInicio ? `${a.horaInicio.slice(0, 5)} · ` : ""}
                  {formatPropertyName(a.propiedad?.nombre) || `Aseo #${a.id}`}
                </span>
                {tieneNota && (
                  <FileTextOutlined style={{ fontSize: 10, color: "#0ea5e9" }} />
                )}
                <span
                  style={{
                    fontSize: 10,
                    color: "#0369a1",
                    whiteSpace: "nowrap",
                    fontWeight: 600,
                  }}
                >
                  {fmtClp(a.sueldoAseador)}
                </span>
              </div>
            </li>
          );
        })}
        {items.length > maxChips && (
          <li className="ca-cell-more">
            <a
              onClick={(e) => {
                e.stopPropagation();
                setDayModal(date);
              }}
            >
              +{items.length - maxChips} más
            </a>
          </li>
        )}
      </ul>
    );
  };

  const handleSelect = (date: Dayjs, info: { source: string }) => {
    // AntD dispara `select` también al cambiar de mes con las flechas; sólo
    // abrimos el detalle del día cuando el usuario clickea explícitamente una
    // celda con aseos (el click en un chip puntual ya abre sus notas directo).
    if (info?.source !== "date") return;
    const key = date.format("YYYY-MM-DD");
    if ((aseosPorFecha.get(key) ?? []).length === 0) return;
    setDayModal(date);
  };

  const handlePanelChange = (date: Dayjs) => {
    setRefMonth(date);
  };

  const dayModalItems = dayModal
    ? aseosPorFecha.get(dayModal.format("YYYY-MM-DD")) ?? []
    : [];

  return (
    <Layout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar}>
      <div className="ca-page">
        <PageHeader
          title="Calendario"
          helpText="Vista mensual de tus aseos: cada tarjeta muestra la propiedad, el pago y si tiene instrucciones. Toca un aseo para ver/editar sus notas (fotos, videos y documentos). En «Aseos» tienes la tabla completa con acciones."
        />

        {error ? (
          <Alert
            type="error"
            message="Error"
            description={error}
            showIcon
            action={
              <Button size="small" onClick={() => void load(refMonth)}>
                Reintentar
              </Button>
            }
            style={{ marginBottom: 12 }}
          />
        ) : null}

        {loading ? (
          <div className="ca-loading">
            <Spin size="large" />
          </div>
        ) : (
          <div className="ca-card">
            <Calendar
              value={refMonth}
              onSelect={handleSelect}
              onPanelChange={handlePanelChange}
              cellRender={(current, info) => {
                if (info.type !== "date") return info.originNode;
                return renderCellContent(current as Dayjs);
              }}
            />
          </div>
        )}

        {/* Listado del día (al tocar el día o "+N más"). Cada aseo abre su
            modal de NOTAS — el mismo esquema que la tabla de /aseos. */}
        <Modal
          open={dayModal !== null}
          title={
            dayModal
              ? `Aseos del ${dayModal.format("dddd D [de] MMMM [de] YYYY")}`
              : ""
          }
          onCancel={() => setDayModal(null)}
          footer={<Button onClick={() => setDayModal(null)}>Cerrar</Button>}
          width={520}
          destroyOnClose
        >
          {dayModalItems.length === 0 ? (
            <Text type="secondary">Sin aseos este día.</Text>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Toca un aseo para ver sus notas: instrucciones del administrador y
                tus observaciones (fotos, videos y documentos).
              </Text>
              {dayModalItems.map((a) => (
                <div
                  key={a.id}
                  role="button"
                  onClick={() => {
                    setDayModal(null);
                    setNotesAseo(a);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                    <strong style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                      {formatPropertyName(a.propiedad?.nombre) || `Aseo #${a.id}`}
                    </strong>
                    {/* Códigos: identidad del aseo + arriendo vinculado (o «Manual»). */}
                    <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                      <AseoCodeTag id={a.id} withTooltip={false} />
                      {a.reservaId != null ? (
                        <EstadiaCodeTag id={a.reservaId} withTooltip={false} />
                      ) : (
                        <span style={{ fontSize: 11, color: "#9ca3af" }}>Manual</span>
                      )}
                    </div>
                    <span style={{ fontSize: 12, color: "#6b7280" }}>
                      {a.horaInicio ? `${a.horaInicio.slice(0, 5)} · ` : ""}
                      Pago {fmtClp(a.sueldoAseador)}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {a.urgencia && (
                      <Tag color={URGENCIA_COLOR[a.urgencia] ?? "default"} style={{ margin: 0 }}>
                        {String(a.urgencia).toUpperCase()}
                      </Tag>
                    )}
                    <Tag
                      style={{ margin: 0 }}
                      color={
                        ESTADO_COLOR[a.estado] === "success"
                          ? "green"
                          : ESTADO_COLOR[a.estado] === "processing"
                            ? "blue"
                            : "orange"
                      }
                    >
                      {ESTADO_LABEL[a.estado] ?? a.estado}
                    </Tag>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>

        {/* Modal de NOTAS de un aseo — MISMO componente/esquema que la tabla de
            /aseos (AseoNotesSection). El aseador lee la nota "para el aseador" y
            edita la suya (con autoguardado + subida de fotos/videos/docs). */}
        <NotasContenedor
          open={notesAseo !== null}
          aseoId={notesAseo?.id}
          title={
            notesAseo
              ? `Notas del aseo — ${
                  formatPropertyName(notesAseo.propiedad?.nombre) || `#${notesAseo.id}`
                }`
              : "Notas del aseo"
          }
          onClose={() => setNotesAseo(null)}
          width={620}
        >
          {notesAseo && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                  marginBottom: 12,
                }}
              >
                {/* Códigos del aseo: su identidad propia (#N) y el arriendo
                    vinculado («Estadía #N»), para cruzarlo con el resto del sistema. */}
                <AseoCodeTag id={notesAseo.id} withTooltip={false} />
                {notesAseo.reservaId != null ? (
                  <EstadiaCodeTag id={notesAseo.reservaId} withTooltip={false} />
                ) : (
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>Sin arriendo (manual)</span>
                )}
                <Tag
                  color={
                    ESTADO_COLOR[notesAseo.estado] === "success"
                      ? "green"
                      : ESTADO_COLOR[notesAseo.estado] === "processing"
                        ? "blue"
                        : "orange"
                  }
                >
                  {ESTADO_LABEL[notesAseo.estado] ?? notesAseo.estado}
                </Tag>
                {notesAseo.urgencia && (
                  <Tag color={URGENCIA_COLOR[notesAseo.urgencia] ?? "default"}>
                    {String(notesAseo.urgencia).toUpperCase()}
                  </Tag>
                )}
                {notesAseo.horaInicio && (
                  <span style={{ fontSize: 13 }}>
                    <strong>Hora:</strong> {notesAseo.horaInicio.slice(0, 5)}
                  </span>
                )}
                <span style={{ fontSize: 13 }}>
                  <strong>Pago:</strong> {fmtClp(notesAseo.sueldoAseador)}
                </span>
              </div>
              <AseoNotesSection aseoId={notesAseo.id} />
            </>
          )}
        </NotasContenedor>
      </div>
    </Layout>
  );
}
